// @ts-strict-ignore
import { ReactElement, useEffect } from 'react';
import Experiment, { When, useExperimentBucket } from '@carvana/experiment';
import Cookie from '@carvana/stc-cookie';
import { Environment } from '@carvana/experiment/SharedTypes';
import { getUserState } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';

import experimentHelper, { ExperimentType, Experiments } from '../shared/experimentHelper';

declare global {
  interface Window {
    FS: {
      setUserVars: (vars: { [x: string]: string }) => void;
    };
  }
}

export type LinkedExperiment = {
  experimentName: string;
  identifierType: string;
};

export type AdditionalExperimentProps = {
  token?: string;
  nonBucketedExperiment?: boolean;
  linkedExperiment?: LinkedExperiment;
  linkedIdentifier?: string;
};

type WithExperimentProps = {
  BUCKETS: string[];
  IDENTIFIER: string;
  NAME: string;
  NON_BUCKETED?: boolean;
  showLoadingIndicator?: boolean;
  children?: ReactElement;
};

const getIdentifier = (type, userId) =>
  type.toLowerCase() === 'userid' ? userId || getUserState().user.user_id : Cookie.getCookie(type);

const isEqual = (a: Experiments | null, b: Experiments | null) => {
  if (!a || !b) return false;
  if (a === b) return true;
  if (JSON.stringify(a) === JSON.stringify(b)) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(key => a[key] === b[key]);
};

const storeExperimentsInDatadog = (experiment: ExperimentType) => {
  experimentHelper.addExperiment(experiment);
  const experimentsAsObject = experimentHelper.getAllExperiments();
  const existingExperiments = (datadogRum.getGlobalContext().auExperiments as Experiments) ?? null;
  if (!isEqual(existingExperiments, experimentsAsObject)) {
    datadogRum.setGlobalContextProperty('huExperiments', experimentsAsObject);
  }
};

const WithExperiment = ({
  BUCKETS,
  IDENTIFIER,
  NAME,
  NON_BUCKETED = undefined,
  children
}: WithExperimentProps) => {
  const ENVIRONMENT = process.env.CARVANA_APP_ENVIRONMENT as Environment;

  const userIdIdentifier = getUserState().user.user_id;
  const identifier = getIdentifier(IDENTIFIER, userIdIdentifier);
  const assignedBucket = useExperimentBucket(NAME);

  const experimentProps: AdditionalExperimentProps = {
    token: null,
    nonBucketedExperiment: NON_BUCKETED
  };

  useEffect(() => {
    if (!assignedBucket || !NAME) {
      return;
    }
    storeExperimentsInDatadog({
      assignedBucket,
      experimentName: NAME
    } as ExperimentType);
    window.FS?.setUserVars({ [`${NAME}_str`]: assignedBucket });
  }, [assignedBucket]);

  if (!identifier) {
    return <>{children}</>;
  }

  return (
    <Experiment
      environment={ENVIRONMENT}
      identifier={identifier}
      loading={null}
      name={NAME}
      {...experimentProps}
    >
      <When bucket={BUCKETS}>{children}</When>
    </Experiment>
  );
};

export default WithExperiment;
